import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getOverview } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import { OverviewModel } from '@/types/clarityLens'

type ChartDataModel = {
  data: Record<string, number>
  labels: string[]
}

export default function useOverview(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemName?.trim()),
    [startDate, endDate, branches, itemName]
  )
  const loading = !isReady || isFetching

  const [supplierMixChartData, setSupplierMixChartData] =
    useState<ChartDataModel>()
  const [manufacturerBreakdownChartData, setManufacturerBreakdownChartData] =
    useState<ChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const { data } = await fetchOverviewData(
        branches,
        startDate,
        endDate,
        itemName
      )

      const supplierMixChartData: ChartDataModel | undefined =
        data?.supplierMix && Object.keys(data.supplierMix).length > 0
          ? {
              data: data.supplierMix,
              labels: Object.keys(data.supplierMix),
            }
          : undefined

      const manufacturerBreakdownChartData: ChartDataModel | undefined =
        data?.manufacturerBreakdown &&
        Object.keys(data.manufacturerBreakdown).length > 0
          ? {
              data: data.manufacturerBreakdown,
              labels: Object.keys(data.manufacturerBreakdown),
            }
          : undefined

      setSupplierMixChartData(supplierMixChartData)
      setManufacturerBreakdownChartData(manufacturerBreakdownChartData)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching overview data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    supplierMixChartData,
    manufacturerBreakdownChartData,
    loading,
    refetch,
  }
}

async function fetchOverviewData(
  branches: string,
  startDate: string,
  endDate: string,
  itemName: string
): Promise<{
  data: OverviewModel
}> {
  try {
    const response = await getOverview(branches, startDate, endDate, itemName)

    const data: OverviewModel = convertData(
      response.supplier_mix,
      response.manufacturer_breakdown,
      response.sku_concentration
    )

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  supplierMix: { [key: string]: number },
  manufacturerBreakdown: { [key: string]: number },
  skuConcentration: { rest: string; preferred_sku_count: number }
): OverviewModel {
  const formattedSupplierMix = Object.fromEntries(
    Object.entries(supplierMix).map(([key, value]) => [
      key,
      formatMetric(value, 2),
    ])
  )

  const formattedManufacturerBreakdown = Object.fromEntries(
    Object.entries(manufacturerBreakdown).map(([key, value]) => [
      key,
      formatMetric(value, 2),
    ])
  )

  return {
    supplierMix: formattedSupplierMix,
    manufacturerBreakdown: formattedManufacturerBreakdown,
    skuConcentration: {
      rest: skuConcentration.rest,
      preferredSkuCount: formatMetric(skuConcentration.preferred_sku_count, 2),
    },
  }
}
